import React, { useEffect, useRef, useState } from "react";
import { Loader2, Save, Upload, Building2, Gift } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

const FIELDS = [
  ["raison_sociale", "Raison sociale"], ["forme", "Forme juridique"], ["capital", "Capital"], ["rcs", "RCS"], ["siret", "SIRET"], ["tva", "N° de TVA"],
  ["adresse", "Adresse du siège"], ["code_postal", "Code postal"], ["ville", "Ville"], ["representant", "Représentant"], ["fonction", "Fonction"],
  ["telephone", "Téléphone"], ["telephone_conseiller", "Téléphone conseiller"], ["email_contact", "Email de contact"],
];
const input = "w-full px-2.5 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";

function ImageSlot({ fileKey, label, version, onUploaded, wide }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    let u;
    fetch(`${API}/entreprise/files/${fileKey}`, { headers: { Authorization: `Bearer ${localStorage.getItem("norea_token")}` } })
      .then((r) => (r.ok ? r.blob() : null)).then((b) => { if (b) { u = URL.createObjectURL(b); setUrl(u); } }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [fileKey, version]);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post(`/entreprise/files/${fileKey}`, fd); toast.success(`${label} remplacé`); onUploaded(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-1.5" data-testid={`company-file-${fileKey}`}>
      <div className="text-xs font-medium text-norea-text">{label}</div>
      <div className={`${wide ? "aspect-video" : "h-28"} rounded border border-norea-border bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] flex items-center justify-center overflow-hidden`}>
        {url ? <img src={url} alt={label} className={wide ? "w-full h-full object-cover" : "max-h-full max-w-full object-contain p-2"} /> : <span className="text-xs text-norea-textMuted">Aucune image</span>}
      </div>
      <button onClick={() => ref.current?.click()} disabled={busy} className="btn-secondary text-xs" data-testid={`company-upload-${fileKey}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Remplacer
      </button>
      <input ref={ref} type="file" accept="image/png,image/jpeg" onChange={upload} className="hidden" />
    </div>
  );
}

export default function EntreprisePanel() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  useEffect(() => { api.get("/entreprise").then((r) => setForm(r.data)).catch(() => toast.error("Chargement impossible")); }, []);
  if (!form) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try { const { files, ...body } = form; const r = await api.put("/entreprise", { ...body, parrainage_montant: Number(body.parrainage_montant) || 0 }); setForm(r.data); toast.success("Fiche entreprise enregistrée"); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };
  const bump = () => setVersion((v) => v + 1);
  return (
    <div className="space-y-6" data-testid="entreprise-panel">
      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-medium flex items-center gap-2"><Building2 className="w-4 h-4" /> Fiche entreprise</h3>
          <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="company-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer</button>
        </div>
        <p className="text-xs text-norea-textMuted mb-4">Ces informations apparaissent sur tous les documents générés (ordres de mission, attestations, PV…).</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {FIELDS.map(([k, l]) => (
            <label key={k} className="text-xs text-norea-textMuted space-y-1">
              <span>{l}</span>
              <input value={form[k] || ""} onChange={set(k)} className={input} data-testid={`company-${k}`} />
            </label>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
          <ImageSlot fileKey="logo" label="Logo" version={version} onUploaded={bump} />
          <ImageSlot fileKey="tampon" label="Tampon + signature" version={version} onUploaded={bump} />
          <ImageSlot fileKey="tampon_seul" label="Tampon seul (variante)" version={version} onUploaded={bump} />
        </div>
      </div>
      <div className="card-flat p-5" data-testid="parrainage-section">
        <h3 className="font-heading text-lg font-medium flex items-center gap-2 mb-4"><Gift className="w-4 h-4" /> Parrainage</h3>
        <label className="text-xs text-norea-textMuted space-y-1 block max-w-[200px]">
          <span>Montant du parrainage (€)</span>
          <input type="number" value={form.parrainage_montant ?? 500} onChange={set("parrainage_montant")} className={input} data-testid="company-parrainage_montant" />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4">
          <ImageSlot fileKey="parrainage_PV" label="Photo parrainage PV" version={version} onUploaded={bump} wide />
          <ImageSlot fileKey="parrainage_ITE" label="Photo parrainage ITE" version={version} onUploaded={bump} wide />
          <ImageSlot fileKey="parrainage_PAC" label="Photo parrainage PAC" version={version} onUploaded={bump} wide />
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={save} disabled={saving} className="btn-secondary text-sm" data-testid="parrainage-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer le montant</button>
        </div>
      </div>
    </div>
  );
}
